import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { auth } from "@/lib/api";
import { LogOut, Car, MessageSquare, Plus, Menu, X, ChevronRight, Home } from "lucide-react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_8465ce77-9585-4fff-bcf3-0cae1d417cf9/artifacts/w0kkj7ub_WhatsApp_Image_2026-02-27_at_19.16.07-removebg-preview%20%282%29.png";

const NAV = [
  { to: "/admin", label: "Veicoli", icon: Car, exact: true },
  { to: "/admin/messages", label: "Messaggi", icon: MessageSquare },
];

export default function AdminLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const logout = () => {
    auth.logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] text-[#1e293b] flex">
      {/* ============ DESKTOP SIDEBAR ============ */}
      <aside
        data-testid="admin-sidebar"
        className="hidden lg:flex w-64 bg-white border-r border-slate-200 flex-col fixed inset-y-0 left-0 z-30"
      >
        <Link to="/admin" className="px-6 py-7 flex items-center gap-3 border-b border-slate-100">
          <img
            src={LOGO_URL}
            alt="L"
            className="h-11 w-11 rounded-xl bg-white ring-1 ring-[#76BA1B]/30"
          />
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#76BA1B] font-bold">
              Admin
            </div>
            <div className="font-heading text-base font-bold leading-tight text-[#1e293b]">
              Minicarmobilità
            </div>
          </div>
        </Link>

        <nav className="flex-1 px-3 py-5 space-y-1">
          {NAV.map(({ to, label, icon: Icon, exact }) => {
            const active = exact
              ? location.pathname === to
              : location.pathname.startsWith(to);
            return (
              <Link
                key={to}
                to={to}
                data-testid={`admin-nav-${label.toLowerCase()}`}
                className={`group flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all ${
                  active
                    ? "bg-[#76BA1B] text-white shadow-[0_8px_24px_rgba(118,186,27,0.35)]"
                    : "text-slate-600 hover:bg-slate-100 hover:text-[#1e293b]"
                }`}
              >
                <Icon size={16} />
                <span className="flex-1">{label}</span>
                {active && <ChevronRight size={14} />}
              </Link>
            );
          })}
        </nav>

        <div className="px-3 py-3 border-t border-slate-100 space-y-1">
          <Link
            to="/admin/vehicle/new"
            data-testid="sidebar-new-vehicle"
            className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#76BA1B] text-white text-sm font-bold hover:bg-[#5fa015] hover:shadow-[0_8px_24px_rgba(118,186,27,0.45)] transition-all"
          >
            <Plus size={16} /> Nuovo veicolo
          </Link>
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs text-slate-500 hover:bg-slate-100 hover:text-[#1e293b] transition"
          >
            <Home size={13} /> Apri sito pubblico
          </a>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="w-full flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs text-red-500 hover:bg-red-50 transition"
          >
            <LogOut size={13} /> Esci
          </button>
        </div>
      </aside>

      {/* ============ MOBILE/TABLET TOP BAR ============ */}
      <header
        className="lg:hidden sticky top-0 z-40 bg-white border-b border-slate-200 w-full"
        data-testid="admin-topbar"
      >
        <div className="px-4 h-14 flex items-center justify-between">
          <button
            onClick={() => setOpen(true)}
            className="p-2 rounded-lg hover:bg-slate-100"
            aria-label="Apri menu"
            data-testid="admin-burger"
          >
            <Menu size={20} />
          </button>
          <Link to="/admin" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="L" className="h-7 w-7 rounded-lg bg-white ring-1 ring-[#76BA1B]/30" />
            <span className="font-heading font-bold text-sm text-[#1e293b]">Minicarmobilità · Admin</span>
          </Link>
          <button
            onClick={logout}
            className="p-2 rounded-lg hover:bg-red-50 text-red-500"
            aria-label="Esci"
          >
            <LogOut size={18} />
          </button>
        </div>
      </header>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex" data-testid="admin-mobile-drawer">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative w-72 max-w-[85vw] bg-white border-r border-slate-200 flex flex-col animate-fade-up">
            <div className="px-5 py-5 flex items-center justify-between border-b border-slate-100">
              <div className="flex items-center gap-2">
                <img src={LOGO_URL} alt="L" className="h-9 w-9 rounded-lg bg-white ring-1 ring-[#76BA1B]/30" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#76BA1B] font-bold">Admin</div>
                  <div className="font-heading font-bold leading-tight text-[#1e293b]">Minicarmobilità</div>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="p-2 rounded-lg hover:bg-slate-100"><X size={18} /></button>
            </div>
            <nav className="flex-1 px-3 py-4 space-y-1">
              {NAV.map(({ to, label, icon: Icon, exact }) => {
                const active = exact ? location.pathname === to : location.pathname.startsWith(to);
                return (
                  <Link
                    key={to}
                    to={to}
                    onClick={() => setOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold ${
                      active
                        ? "bg-[#76BA1B] text-white"
                        : "text-slate-600 hover:bg-slate-100"
                    }`}
                  >
                    <Icon size={16} /> {label}
                  </Link>
                );
              })}
            </nav>
            <div className="px-3 py-3 border-t border-slate-100">
              <Link
                to="/admin/vehicle/new"
                onClick={() => setOpen(false)}
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#76BA1B] text-white text-sm font-bold"
              >
                <Plus size={16} /> Nuovo veicolo
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* ============ MAIN CONTENT ============ */}
      <div className="flex-1 lg:ml-64 min-w-0">
        <main className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-10 pb-32 lg:pb-10">
          {children}
        </main>
      </div>

      {/* Mobile floating add button — always reachable */}
      <Link
        to="/admin/vehicle/new"
        data-testid="admin-fab-new-vehicle"
        className="lg:hidden fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 px-5 py-4 rounded-2xl bg-[#76BA1B] text-white font-bold shadow-[0_12px_32px_rgba(118,186,27,0.5)] hover:scale-105 transition"
      >
        <Plus size={20} /> Aggiungi veicolo
      </Link>
    </div>
  );
}
